import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningIcon from '@mui/icons-material/Warning';
import { getVideo } from '../../services/admin/admin-video.service';
import { ADMIN_VIDEO_KEY } from '../../common/utils/video-query-cache';
import { getHttpStatus } from '../../common/utils/http-status';
import {
  isArchived,
  isAwaitingChange,
} from '../../common/utils/video-status-utils';
import { formatVideoDuration } from '../../common/utils/video-file-utils';
import { DeleteVideoAction } from '../../common/model/type/video-placement.types';
import { VideoLibraryLocationState } from '../../common/model/type/video-list-filter.types';
import { useVideoLibraryPath } from '../../hooks/use-video-library-path';
import { useVideoPermissions } from '../../hooks/use-video-permissions';
import { VIDEO_STATUS_POLL_MS } from '../../hooks/use-video-status-polling';
import { useVideoUploads } from '../../hooks/use-video-uploads';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvVideoLink from '../UI/atoms/OvVideoLink';
import OvDeleteVideoDialog from '../UI/molecules/OvDeleteVideoDialog';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvVideoStatusChip from '../UI/molecules/OvVideoStatusChip';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import OvReplaceVideoDialog from '../UI/organisms/OvReplaceVideoDialog';
import OvVideoDetailsNotices from '../UI/organisms/OvVideoDetailsNotices';
import OvVideoDetailsSection from '../UI/organisms/OvVideoDetailsSection';
import OvVideoPosterSection from '../UI/organisms/OvVideoPosterSection';
import OvVideoPreview from '../UI/organisms/OvVideoPreview';
import OvVideoShownInSection from '../UI/organisms/OvVideoShownInSection';

const NOT_FOUND_STATUSES = [400, 404];

const formatDateTime = (value: string) =>
  format(new Date(value), 'd MMM yyyy, HH:mm');

const VideoDetails: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { videoId = '' } = useParams<{ videoId: string }>();
  const libraryPath = useVideoLibraryPath();
  const { canCreate, canUpdate, canDelete } = useVideoPermissions();
  const { uploads } = useVideoUploads();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  const [savedVideoId, setSavedVideoId] = useState<string | null>(null);

  const {
    data: video,
    isPending,
    isError,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [ADMIN_VIDEO_KEY, videoId],
    queryFn: () => getVideo(videoId),
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data && isAwaitingChange(query.state.data)
        ? VIDEO_STATUS_POLL_MS
        : false,
  });

  const backLink = (
    <BackLink to={libraryPath}>
      <ArrowBackIcon aria-hidden="true" />
      {t('app.subPages.menu.videos')}
    </BackLink>
  );

  if (!video) {
    if (isPending) {
      return (
        <Page aria-busy="true">
          <p role="status">{t('videos.details.loading')}</p>
          <OvLoadingIndicator position="fixed" />
        </Page>
      );
    }

    const isNotFound = NOT_FOUND_STATUSES.includes(getHttpStatus(error) ?? 0);

    return (
      <Page>
        <OvRecordDetailsCanvas>
          <OvListStateMessage
            isAlert={!isNotFound}
            icon={<WarningIcon />}
            title={t(
              isNotFound
                ? 'videos.details.notFoundTitle'
                : 'videos.details.errorTitle'
            )}
            description={t(
              isNotFound
                ? 'videos.details.notFoundDescription'
                : 'videos.states.errorDescription'
            )}
          >
            <StateActions>
              {backLink}
              {!isNotFound && (
                <OvCompactButton onClick={() => refetch()}>
                  {t('common.actions.retry')}
                </OvCompactButton>
              )}
            </StateActions>
          </OvListStateMessage>
        </OvRecordDetailsCanvas>
      </Page>
    );
  }

  const upload = uploads.find((item) => item.videoId === video.id);
  const archived = isArchived(video);
  const isEditable = canUpdate && !archived;
  const isReadOnly = !canCreate && !canUpdate && !canDelete;
  const canReplace =
    canCreate &&
    canUpdate &&
    video.status === 'ready' &&
    !archived &&
    !video.replaced_by_video_id;
  const width = video.width ?? video.source_width;
  const height = video.height ?? video.source_height;
  const isSaved = savedVideoId === video.id;
  const onSaved = () => setSavedVideoId(video.id);

  const handleDeleted = (action: DeleteVideoAction) => {
    const state: VideoLibraryLocationState = { deleted: action };
    navigate(libraryPath, { state });
  };

  return (
    <Page aria-busy={isFetching}>
      <Saved role="status">{isSaved ? t('videos.details.saved') : ''}</Saved>

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('videos.details.breadcrumb')}
          backControl={backLink}
          current={video.title}
          title={video.title}
          titleAside={
            <Chips>
              <OvVideoStatusChip video={video} upload={upload} />
              {archived && (
                <OvStatusChip
                  label={t('videos.status.archived')}
                  tone="neutral"
                />
              )}
            </Chips>
          }
          meta={
            <>
              <span>
                {t('videos.fields.id')} <VideoId>{video.id}</VideoId>
                <OvCopyToClipboardButton
                  text={video.id}
                  label={t('videos.details.copyId')}
                  copiedLabel={t('videos.details.idCopied')}
                  failedLabel={t('videos.details.idCopyFailed')}
                />
              </span>
              <span>
                {t('videos.details.created', {
                  date: formatDateTime(video.created_at),
                })}
              </span>
              <span>
                {t('videos.details.updated', {
                  date: formatDateTime(video.updated_at),
                })}
              </span>
              <span>
                {video.duration_seconds === null
                  ? t('videos.list.durationUnavailable')
                  : t('videos.details.duration', {
                      duration: formatVideoDuration(video.duration_seconds),
                    })}
              </span>
              <span>{t('videos.details.dimensions', { width, height })}</span>
              <IdHint>{t('videos.details.idHint')}</IdHint>
            </>
          }
          actions={
            canDelete &&
            !archived && (
              <OvRecordHeaderActions>
                <OvCompactButton
                  tone="danger"
                  icon={<DeleteIcon />}
                  onClick={() => setIsDeleteOpen(true)}
                >
                  {t('common.actions.delete')}
                </OvCompactButton>
              </OvRecordHeaderActions>
            )
          }
        />
        <OvVideoDetailsNotices
          video={video}
          upload={upload}
          canUpdate={canUpdate}
          canDelete={canDelete}
          isReadOnly={isReadOnly}
          hasRefreshError={isError}
        />
        <Body>
          <PreviewArea>
            <OvVideoPreview video={video}>
              {canReplace && (
                <OvCompactButton
                  tone="secondary"
                  icon={<SwapHorizIcon />}
                  onClick={() => setIsReplaceOpen(true)}
                >
                  {t('videos.replace.action')}
                </OvCompactButton>
              )}
              {video.replaced_by_video_id && (
                <OvVideoLink videoId={video.replaced_by_video_id}>
                  {t('videos.replace.openReplacement')}
                </OvVideoLink>
              )}
            </OvVideoPreview>
          </PreviewArea>
          <Sections>
            <OvVideoDetailsSection
              video={video}
              isEditable={isEditable}
              onSaved={onSaved}
            />
            <OvVideoShownInSection
              video={video}
              upload={upload}
              isEditable={isEditable}
              onSaved={onSaved}
            />
            <OvVideoPosterSection
              video={video}
              isEditable={isEditable}
              onSaved={onSaved}
            />
          </Sections>
        </Body>
      </OvRecordDetailsCanvas>

      {(canReplace || isReplaceOpen) && (
        <OvReplaceVideoDialog
          video={video}
          isOpen={isReplaceOpen}
          onClose={() => setIsReplaceOpen(false)}
        />
      )}
      {canDelete && !archived && isDeleteOpen && (
        <OvDeleteVideoDialog
          video={video}
          onClose={() => setIsDeleteOpen(false)}
          onDeleted={handleDeleted}
        />
      )}
      {isFetching && <OvLoadingIndicator position="fixed" />}
    </Page>
  );
};

export default VideoDetails;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: bold;
  text-decoration: none;

  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const StateActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Variables.spacing.SMALL};
`;

const Saved = styled.p`
  margin: 0;
  color: ${Colours.OV_ACCENT_GREEN};
  font-weight: bold;

  &:empty {
    display: none;
  }
`;

const Chips = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const VideoId = styled.code`
  font-family: monospace;
  overflow-wrap: anywhere;
  user-select: all;
`;

const IdHint = styled.span`
  flex-basis: 100%;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(18.75rem, 1fr);

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PreviewArea = styled.div`
  min-width: 0;
  padding: ${Variables.spacing.LARGE} ${Variables.spacing.XLARGE};
`;

const Sections = styled.div`
  min-width: 0;
  border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  > section + section {
    border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  }

  @media (max-width: ${breakpoints.md}) {
    border-left: 0;
    border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  }
`;
